// Future Counts Index™ Baseline Certificate — client-side print-to-PDF generator.
// Opens a styled document in a new tab and triggers print (Save as PDF).

const DIM_LABELS = {
  self_awareness: "Self-Awareness",
  relational_practice: "Relational Practice",
  decision_making: "Decision-Making",
  systems_thinking: "Systems Thinking",
};

export function generateCertificate({ user, result }) {
  const w = window.open("", "_blank");
  if (!w) return false;

  const maturity = Math.round(Object.values(result.scores).reduce((a, b) => a + b, 0) / 4);
  const dateStr = new Date(result.created_at).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const dimRows = Object.entries(result.scores)
    .map(([k, v]) => `
      <div class="dim-row">
        <div class="dim-label">${DIM_LABELS[k] || k}</div>
        <div class="dim-bar"><div class="dim-fill" style="width:${v}%"></div></div>
        <div class="dim-score">${Math.round(v)}</div>
      </div>`)
    .join("");

  const html = `<!doctype html>
<html><head>
<meta charset="utf-8" />
<title>Future Counts Index™ Certificate — ${user?.name || ""}</title>
<link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&family=Figtree:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: 'Figtree', sans-serif; color: #0B1E36; background: #FDFBF7; }
  .cert {
    width: 297mm; height: 210mm;
    padding: 22mm 24mm;
    background:
      radial-gradient(circle at 12% 10%, rgba(236,72,153,0.10), transparent 40%),
      radial-gradient(circle at 88% 92%, rgba(201,169,97,0.12), transparent 45%),
      #FDFBF7;
    position: relative;
    border: 1px solid #E2E8F0;
  }
  .frame {
    position: absolute; inset: 12mm;
    border: 1.5px solid #0B1E36;
    border-radius: 6mm;
    pointer-events: none;
  }
  .inner-frame {
    position: absolute; inset: 14mm;
    border: 0.5px solid rgba(11,30,54,0.25);
    border-radius: 5mm;
    pointer-events: none;
  }
  .content { position: relative; z-index: 1; height: 100%; display: flex; flex-direction: column; }
  header { display: flex; justify-content: space-between; align-items: flex-start; }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand-mark {
    height: 46px; width: 46px; border-radius: 10px; background: #0B1E36;
    display: grid; place-items: center; color: #C9A961; font-size: 22px; font-weight: 700;
  }
  .brand-text { line-height: 1.1; }
  .brand-name { font-family: 'Merriweather', serif; font-size: 20px; color: #0B1E36; }
  .brand-sub { font-size: 10px; letter-spacing: 3px; color: #64748B; text-transform: uppercase; margin-top: 2px; }
  .date { font-size: 11px; letter-spacing: 2px; color: #64748B; text-transform: uppercase; text-align: right; }

  .hero { text-align: center; margin-top: 24px; }
  .eyebrow { font-size: 11px; letter-spacing: 5px; color: #EC4899; font-weight: 700; text-transform: uppercase; }
  .title { font-family: 'Merriweather', serif; font-size: 42px; font-weight: 700; margin: 10px 0 6px; color: #0B1E36; letter-spacing: -0.5px; }
  .subtitle { font-size: 13px; color: #64748B; max-width: 480px; margin: 0 auto; line-height: 1.5; }

  .awarded { text-align: center; margin-top: 22px; }
  .awarded-label { font-size: 11px; letter-spacing: 4px; color: #94A3B8; text-transform: uppercase; margin-bottom: 8px; }
  .awarded-name { font-family: 'Merriweather', serif; font-size: 36px; color: #EC4899; font-weight: 400; letter-spacing: -0.3px; }
  .awarded-org { font-size: 12px; color: #64748B; margin-top: 4px; }

  .body-grid { display: grid; grid-template-columns: 220px 1fr; gap: 24px; margin-top: 26px; align-items: center; }

  .ring-wrap { display: flex; flex-direction: column; align-items: center; }
  .ring {
    height: 170px; width: 170px; border-radius: 50%;
    background: conic-gradient(#EC4899 0 calc(${maturity} * 1%), #F1EDE5 calc(${maturity} * 1%) 100%);
    display: grid; place-items: center;
    box-shadow: 0 6px 20px rgba(236,72,153,0.12);
  }
  .ring-inner {
    height: 130px; width: 130px; border-radius: 50%; background: #FDFBF7;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .ring-val { font-family: 'Merriweather', serif; font-size: 46px; color: #0B1E36; line-height: 1; }
  .ring-label { font-size: 9px; letter-spacing: 3px; color: #64748B; text-transform: uppercase; margin-top: 6px; }
  .ring-sub { font-size: 10px; color: #94A3B8; margin-top: 12px; letter-spacing: 2px; text-transform: uppercase; }

  .dims { display: flex; flex-direction: column; gap: 10px; }
  .dim-row { display: grid; grid-template-columns: 150px 1fr 40px; gap: 14px; align-items: center; }
  .dim-label { font-size: 12px; font-weight: 600; color: #0B1E36; }
  .dim-bar { height: 8px; background: #F1EDE5; border-radius: 999px; overflow: hidden; }
  .dim-fill { height: 100%; background: linear-gradient(90deg, #EC4899, #C9A961); border-radius: 999px; }
  .dim-score { font-family: 'Merriweather', serif; font-size: 16px; color: #0B1E36; text-align: right; }

  .headline {
    margin-top: 18px;
    padding: 14px 20px;
    border-left: 3px solid #EC4899;
    background: rgba(236,72,153,0.06);
    border-radius: 4px;
    font-family: 'Merriweather', serif;
    font-size: 14px;
    line-height: 1.5;
    color: #0B1E36;
    font-style: italic;
  }

  footer {
    margin-top: auto;
    padding-top: 20px;
    display: flex; justify-content: space-between; align-items: flex-end;
    border-top: 1px solid rgba(11,30,54,0.12);
  }
  .sig-block { display: flex; flex-direction: column; }
  .sig-line { font-family: 'Merriweather', serif; font-style: italic; font-size: 18px; color: #0B1E36; padding-bottom: 4px; border-bottom: 1px solid #0B1E36; min-width: 180px; text-align: center; }
  .sig-cap { font-size: 9px; letter-spacing: 2px; color: #64748B; text-transform: uppercase; margin-top: 6px; text-align: center; }
  .seal {
    display: flex; align-items: center; gap: 10px;
    color: #64748B; font-size: 10px; letter-spacing: 2px; text-transform: uppercase;
  }
  .seal-mark {
    height: 44px; width: 44px; border-radius: 50%;
    border: 1.5px solid #C9A961; color: #C9A961;
    display: grid; place-items: center;
    font-family: 'Merriweather', serif; font-size: 18px; font-weight: 700;
  }

  @media print {
    body { background: #FDFBF7; }
    .no-print { display: none !important; }
  }
  .toolbar {
    position: fixed; top: 12px; right: 12px; z-index: 100;
    background: #0B1E36; color: white; padding: 8px 16px; border-radius: 999px;
    font-family: 'Figtree', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer;
    border: none; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
</style>
</head>
<body>
  <button class="toolbar no-print" onclick="window.print()">Save as PDF</button>
  <div class="cert">
    <div class="frame"></div>
    <div class="inner-frame"></div>
    <div class="content">
      <header>
        <div class="brand">
          <div class="brand-mark">✦</div>
          <div class="brand-text">
            <div class="brand-name">Future Counts</div>
            <div class="brand-sub">OS™ · Future Counts Index™</div>
          </div>
        </div>
        <div class="date">${dateStr}</div>
      </header>

      <div class="hero">
        <div class="eyebrow">Certificate of Future Counts Index™ Baseline</div>
        <div class="title">Emotionally Intelligent Leadership</div>
        <div class="subtitle">This certificate recognizes the completion of the Future Counts OS™ Future Counts Index™ Diagnostic — a research-grounded assessment of four core dimensions of emotionally intelligent educational leadership.</div>
      </div>

      <div class="awarded">
        <div class="awarded-label">Awarded to</div>
        <div class="awarded-name">${escapeHtml(user?.name || "Educational Leader")}</div>
        <div class="awarded-org">${escapeHtml(user?.org_name || "")}</div>
      </div>

      <div class="body-grid">
        <div class="ring-wrap">
          <div class="ring"><div class="ring-inner">
            <div class="ring-val">${maturity}</div>
            <div class="ring-label">of 100</div>
          </div></div>
          <div class="ring-sub">Future Counts Index™</div>
        </div>
        <div>
          <div class="dims">${dimRows}</div>
          <div class="headline">"${escapeHtml(result.summary?.headline || "Your baseline is set — growth starts here.")}"</div>
        </div>
      </div>

      <footer>
        <div class="sig-block">
          <div class="sig-line">Future Counts OS™</div>
          <div class="sig-cap">Issuing Platform</div>
        </div>
        <div class="seal">
          <div class="seal-mark">FC</div>
          <div>Verified · ${new Date().getFullYear()}</div>
        </div>
      </footer>
    </div>
  </div>
  <script>
    // Auto-open print dialog after fonts load
    window.addEventListener('load', () => setTimeout(() => window.print(), 600));
  </script>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
